import React, { useState, useEffect, createContext, useContext } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { clsx } from "clsx";
import {
  Menu,
  X,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  LogOut,
  Settings,
  User,
  type LucideIcon,
} from "lucide-react";
import type { AuthSession, Workspace } from "@/types";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { SidebarWorkspaceSwitcher } from "./workspace/SidebarWorkspaceSwitcher";
import DemoModeBadge from "./DemoModeBadge";
import SystemHealthIndicator from "./SystemHealthIndicator";
import LanguageToggle from "./LanguageToggle";
import { YDMWordmark, BrandFooter } from "./brand";
import HeaderCreditChip from "@/components/HeaderCreditChip";
import { usePendingTargetCount } from "@/hooks/usePendingTargetCount";

// Layout context
interface LayoutContextType {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
  currentWorkspace: Workspace | null;
  session: AuthSession | null;
}

const SIDEBAR_COLLAPSED_KEY = 'ydm-sidebar-collapsed';

/**
 * Pages drawn edge to edge rather than in the shared max-w-7xl column (owner,
 * 2026-10-15): the native dashboards, all five, so they stay one width. On a
 * maximised window Channel Deep-dive's filter row, Campaign type and Campaign
 * included, then fits on one line beside the data pill and Export, as Overview's
 * does, and Creative Performance's twelve columns fit without scrolling. Every
 * other page keeps the column, where forms and text read better.
 */
const FULL_WIDTH_ROUTES = ['/overview', '/channels', '/creatives', '/audience', '/budget'];

const LayoutContext = createContext<LayoutContextType | null>(null);

export const useLayout = () => {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error("useLayout must be used within a Layout provider");
  }
  return context;
};

// Navigation item type
export interface NavItem {
  name: string;
  href: string;
  icon: LucideIcon;
  badge?: string | number;
  children?: NavItem[];
  disabled?: boolean;
}

export interface LayoutProps {
  children: React.ReactNode;
  navigation: NavItem[];
  session?: AuthSession | null;
  onLogout?: () => void;
  logo?: React.ReactNode;
  title?: string;
}

export const Layout: React.FC<LayoutProps> = ({
  children,
  navigation,
  session,
  onLogout,
  logo,
  title = "YDM Platform",
}) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const fullWidth = FULL_WIDTH_ROUTES.some((href) => isAtOrUnder(pathname, href));
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Desktop-only: collapsed sidebar shows icons-only. Persisted in
  // localStorage so the user's choice survives reloads.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  useEffect(() => {
    try {
      const stored = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
      if (stored === 'true') setSidebarCollapsed(true);
    } catch {
      // localStorage blocked — start expanded
    }
  }, []);
  const toggleSidebarCollapsed = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
      } catch {
        // ignore
      }
      return next;
    });
  };

  const { workspace } = useWorkspace();
  // DEC5: target proposals waiting for an admin. The master is synthesised as
  // 'admin' in userRole, so this one check covers both. null (unknown or
  // failed) leaves the row's own badge alone — never a fake 0.
  const isAdminish = workspace?.userRole === "admin";
  const pendingTargets = usePendingTargetCount(workspace?.id ?? null, isAdminish);
  const navItems = navigation.map((item) =>
    item.href === "/settings" && pendingTargets && pendingTargets > 0 ? { ...item, badge: pendingTargets } : item
  );

  return (
    <LayoutContext.Provider
      value={{ sidebarOpen, setSidebarOpen, sidebarCollapsed, setSidebarCollapsed, currentWorkspace: workspace, session: session || null }}
    >
      <div className="min-h-screen bg-gray-50">
        {/* Skip to content link — visible on focus only */}
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary-500 focus:text-ink-950 focus:font-semibold focus:rounded-lg focus:shadow-lg"
        >
          {t('common.skipToContent')}
        </a>
        {/* Mobile sidebar backdrop */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-gray-900 bg-opacity-50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
            aria-hidden="true"
          />
        )}

        {/* Sidebar — dark YDM theme with yellow logo + accents */}
        <aside
          role="complementary"
          aria-label={t("common.sidebar")}
          className={clsx(
            "fixed top-0 left-0 z-50 h-full bg-ink-950 text-white border-r border-ink-800 print:hidden",
            "transition-[transform,width] duration-300 ease-in-out",
            // Width: collapsed = 4rem (icons only), expanded = 16rem
            sidebarCollapsed ? "lg:w-16" : "lg:w-64",
            // Mobile uses full 16rem regardless of collapse state.
            "w-64",
            sidebarOpen ? "translate-x-0" : "-translate-x-full",
            "lg:translate-x-0"
          )}
        >
          <div className="flex flex-col h-full">
            {/* Logo — yellow on black. Hide the wordmark text when collapsed,
                keep the mark only. */}
            <div className={clsx(
              "flex items-center h-16 border-b border-ink-800",
              sidebarCollapsed ? "lg:justify-center lg:px-2 justify-between px-6" : "justify-between px-6"
            )}>
              <div className={clsx(sidebarCollapsed && "lg:contents")}>
                {sidebarCollapsed
                  ? <YDMWordmark variant="mark" bg="dark" size="md" className="hidden lg:inline-block" />
                  : null}
                {!sidebarCollapsed && (logo ?? <YDMWordmark variant="full" bg="dark" size="md" />)}
                {/* Mobile always sees full logo regardless of collapse */}
                {sidebarCollapsed && (
                  <span className="lg:hidden">
                    {logo ?? <YDMWordmark variant="full" bg="dark" size="md" />}
                  </span>
                )}
              </div>
              <button
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden text-ink-400 hover:text-primary-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded-lg"
                aria-label={t("common.closeNav")}
              >
                <X size={20} aria-hidden="true" />
              </button>
            </div>

            {/* Workspace switcher — hidden when collapsed on desktop.
                Reads WorkspaceContext directly. It used to render
                session.user.memberships, which the auth layer produced by
                mapping each workspace onto an "Organization" 1:1 — so this
                control has always been a workspace switcher wearing the parent
                monorepo's vocabulary, listing the same rows twice over. */}
            {workspace && (
              <div className={clsx(
                "px-4 py-3 border-b border-ink-800",
                sidebarCollapsed && "lg:hidden"
              )}>
                <SidebarWorkspaceSwitcher />
              </div>
            )}

            {/* Navigation */}
            <nav aria-label={t("common.mainNav")} className={clsx(
              "flex-1 py-6 space-y-1 overflow-y-auto scrollbar-thin",
              sidebarCollapsed ? "lg:px-2 px-4" : "px-4"
            )}>
              {navItems.map((item) => (
                <NavItemComponent key={item.href} item={item} collapsed={sidebarCollapsed} onExpandSidebar={() => setSidebarCollapsed(false)} />
              ))}
            </nav>

            {/* Collapse toggle — desktop only, sits above the user menu */}
            <div className="hidden lg:flex border-t border-ink-800 px-2 py-2">
              <button
                onClick={toggleSidebarCollapsed}
                className="w-full flex items-center justify-center py-2 text-ink-400 hover:bg-ink-900 hover:text-primary-500 rounded-lg transition-colors"
                aria-label={sidebarCollapsed ? t("common.expandSidebar") : t("common.collapseSidebar")}
                title={sidebarCollapsed ? t("common.expandSidebar") : t("common.collapseSidebar")}
              >
                {sidebarCollapsed ? <ChevronsRight size={16} /> : <ChevronsLeft size={16} />}
              </button>
            </div>

            {/* User Menu */}
            {session && (
              <div className={clsx(
                "border-t border-ink-800",
                sidebarCollapsed ? "lg:p-2 p-4" : "p-4"
              )}>
                <div className={clsx(
                  "flex items-center",
                  sidebarCollapsed ? "lg:justify-center space-x-3 lg:space-x-0" : "space-x-3"
                )}>
                  <div className="w-10 h-10 bg-primary-500 rounded-full flex items-center justify-center shrink-0" title={session.user.email}>
                    <User size={20} className="text-ink-950" />
                  </div>
                  <div className={clsx("flex-1 min-w-0", sidebarCollapsed && "lg:hidden")}>
                    <p className="text-sm font-medium text-white truncate">
                      {[session.user.firstName, session.user.lastName].filter(Boolean).join(" ")}
                    </p>
                    <p className="text-xs text-ink-400 truncate">
                      {session.user.email}
                    </p>
                  </div>
                </div>
                <div className={clsx("flex items-center space-x-2 mt-3", sidebarCollapsed && "lg:hidden")}>
                  {/* A Link, not a button: this was a <button> with no onClick,
                      sitting beside a Logout that works, so pressing it did
                      nothing. /settings is a real page — unlike Insights, which
                      has none — so the fix is to go there, and as a link it also
                      supports open-in-new-tab. */}
                  <Link
                    to="/settings"
                    className="flex-1 flex items-center justify-center px-3 py-2 text-xs text-ink-300 hover:bg-ink-800 hover:text-primary-500 rounded-lg transition-colors"
                  >
                    <Settings size={14} className="mr-1.5" />
                    {t("common.settings")}
                  </Link>
                  <button
                    onClick={onLogout}
                    className="flex-1 flex items-center justify-center px-3 py-2 text-xs text-error-400 hover:bg-error-900/40 hover:text-error-300 rounded-lg transition-colors"
                  >
                    <LogOut size={14} className="mr-1.5" />
                    {t("common.logout")}
                  </button>
                </div>
                {/* Collapsed: icon-only logout */}
                {sidebarCollapsed && (
                  <button
                    onClick={onLogout}
                    className="hidden lg:flex w-full mt-2 items-center justify-center py-2 text-xs text-error-400 hover:bg-error-900/40 hover:text-error-300 rounded-lg transition-colors"
                    title={t("common.logout")}
                  >
                    <LogOut size={14} />
                  </button>
                )}
              </div>
            )}
          </div>
        </aside>

        {/* Main content — left padding shifts with sidebar width on desktop.
            Flex column at viewport height with main as the growing row: on a
            short page (e.g. Settings → Workspace) the footer used to sit right
            under the content, floating mid-screen with page background below
            it — this pins it to the bottom without moving it on tall pages. */}
        <div className={clsx(
          "min-h-screen flex flex-col transition-[padding] duration-300 ease-in-out print:!pl-0",
          sidebarCollapsed ? "lg:pl-16" : "lg:pl-64"
        )}>
          {/* Demo Data Banner */}
          <DemoModeBadge variant="banner" fullWidth={fullWidth} />
          {/* Top bar */}
          <header role="banner" className="print:hidden sticky top-0 z-30 bg-white border-b border-gray-200 h-16 flex items-center px-4 lg:px-8">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-gray-600 hover:text-gray-700 mr-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-950 focus-visible:ring-offset-2 rounded-lg"
              aria-label={t("common.openNav")}
              aria-expanded={sidebarOpen}
            >
              <Menu size={24} aria-hidden="true" />
            </button>

            <div className="flex-1 flex items-center justify-between">
              <div>
                <h1 className="text-lg font-semibold text-gray-900">{title}</h1>
              </div>

              <div className="flex items-center space-x-4">
                <DemoModeBadge variant="subtle" />
                {/* The workspace's balance, visible on every page — replaces
                    the per-page hero chips. Price labels stay beside the
                    actions they price. */}
                <HeaderCreditChip />
                <LanguageToggle />
                {/* Was a hardcoded "Online" pill: a green pulsing dot on every
                    page for every user, checking nothing. A platform connection
                    could lapse and the warehouse fall days behind while it
                    reassured. It now reports what the existing checks actually
                    say — and says "ตรวจสอบสถานะไม่ได้" when it could not look,
                    rather than borrowing the healthy state. */}
                <SystemHealthIndicator workspaceId={workspace?.id} />
              </div>
            </div>
          </header>

          {/* Page content */}
          {/* Shared page width: every page is capped and centered HERE — pages must
              not carry their own max-w/mx-auto/min-h-screen wrappers, that is what
              made /campaigns stretch edge-to-edge while /mmm sat in a column. The
              exceptions are listed here too: FULL_WIDTH_ROUTES. */}
          <main
            id="main-content"
            role="main"
            className={clsx("w-full mx-auto flex-1 p-4 lg:p-8 animate-page-enter", !fullWidth && "max-w-7xl")}
          >
            {children}
          </main>

          {/* Brand footer — visible on every authenticated page */}
          <BrandFooter variant="full" />
        </div>
      </div>
    </LayoutContext.Provider>
  );
};

// Nav Item Component — uses Link for SPA navigation, useLocation for active state
// Exported for unit testing (the collapsed-rail sub-menu behaviour). Rendered
// internally by Layout; only depends on the router, so it tests in isolation.
/**
 * Whether a path is at, or inside, a nav entry's own route.
 *
 * Children were compared with `===`, which was right while every child route was
 * a leaf. /admin/ai has tabs under it, so on /admin/ai/usage the exact test
 * failed: the Super Admin group did not auto-expand and its AI row was not
 * highlighted — a master landed on the screen with the menu insisting they were
 * somewhere else.
 *
 * The boundary matters. A bare startsWith would let /admin/ai claim
 * /admin/ai-usage, which is a different route that happens to share a prefix.
 */
const isAtOrUnder = (pathname: string, href: string): boolean =>
  pathname === href || pathname.startsWith(`${href}/`);

export const NavItemComponent: React.FC<{ item: NavItem; collapsed?: boolean; onExpandSidebar?: () => void }> = ({ item, collapsed = false, onExpandSidebar }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [expanded, setExpanded] = useState(false);
  const hasChildren = item.children && item.children.length > 0;
  const isDisabled = item.disabled;
  const isActive = location.pathname === item.href ||
    (item.href !== '/' && location.pathname.startsWith(item.href));
  const childActive = item.children?.some((child) => isAtOrUnder(location.pathname, child.href));

  // Auto-expand if a child route is active
  useEffect(() => {
    if (childActive && !expanded) setExpanded(true);
  }, [childActive, expanded]);

  const linkContent = (
    <>
      <item.icon
        size={20}
        className={clsx(
          "shrink-0",
          // Active row is bg-primary-500 (see .nav-item-active). A primary-500 icon
          // on a primary-500 background is invisible — match the active label's dark
          // ink instead so the icon reads against the fill.
          isActive ? "text-ink-950" : "text-ink-400 group-hover:text-primary-500"
        )}
      />
      <span className={clsx("flex-1", collapsed && "lg:hidden")}>{item.name}</span>
      {isDisabled && !hasChildren && !collapsed && (
        <span className="lg:hidden inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-ink-800 text-ink-300 rounded-full">
          {t("common.soon")}
        </span>
      )}
      {item.badge && (
        <span className={clsx(
          "px-2 py-0.5 text-xs bg-primary-100 text-ink-900 rounded-full",
          collapsed && "lg:hidden"
        )}>
          {item.badge}
        </span>
      )}
      {!collapsed && (
        <span
          className={clsx(
            "text-xs",
            isActive
              // No text-primary-500 here: on the active row it would be
              // primary-500 on the primary-500 fill and vanish, the same defect
              // as the icon. Drop the colour so it inherits the row's text-ink-950.
              ? "animate-nav-nudge"
              : "opacity-0 group-hover:opacity-100 transition-opacity text-ink-400"
          )}
          aria-hidden="true"
        >
          »
        </span>
      )}
      {hasChildren && !collapsed && (
        <ChevronRight
          size={16}
          className={clsx(
            "transition-transform",
            // Dark on the active fill (like the icon/label/nudge); gray otherwise.
            isActive ? "text-ink-950" : "text-ink-400",
            expanded && "transform rotate-90"
          )}
        />
      )}
    </>
  );

  const linkClassName = clsx(
    isActive ? "nav-item-active" : "nav-item-inactive group",
    // w-full so parent-with-children rows (rendered as <button>, which shrink-wraps
    // to fit-content) fill the row like <a> rows (which stretch) do. Without it the
    // button rows are narrower, and when collapsed justify-center lands their icon
    // off the shared centre — the icon rail looks jagged.
    "w-full",
    isDisabled && "cursor-not-allowed opacity-60",
    collapsed && "lg:justify-center lg:px-2",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-ink-950 rounded-lg"
  );

  return (
    <div>
      {hasChildren ? (
        <button
          type="button"
          onClick={() => {
            // Desktop collapsed rail: the sub-menu can't render in the 4rem strip
            // (it's lg:hidden below), so clicking a parent expands the sidebar and
            // opens it. Mobile / expanded desktop just toggle the inline list.
            const desktop =
              typeof window !== "undefined" &&
              typeof window.matchMedia === "function" &&
              window.matchMedia("(min-width: 1024px)").matches;
            if (collapsed && desktop && onExpandSidebar) {
              onExpandSidebar();
              setExpanded(true);
            } else {
              setExpanded((e) => !e);
            }
          }}
          className={linkClassName}
          title={
            collapsed
              ? t("nav.expandToOpen", { name: item.name })
              : isDisabled
                ? t("nav.comingSoon", { name: item.name })
                : undefined
          }
          aria-expanded={expanded}
          // The Link branch below announces this; the branch for items WITH
          // children did not, so "Insights" looked disabled (cursor-not-allowed)
          // while a screen reader read it as an ordinary expandable button.
          aria-disabled={isDisabled || undefined}
          aria-current={isActive || childActive ? "page" : undefined}
        >
          {linkContent}
        </button>
      ) : (
        <Link
          to={item.href}
          className={linkClassName}
          title={collapsed ? item.name : isDisabled ? t("nav.comingSoon", { name: item.name }) : undefined}
          aria-current={isActive ? "page" : undefined}
          aria-disabled={isDisabled || undefined}
          onClick={(e) => {
            if (isDisabled) {
              e.preventDefault();
            }
          }}
        >
          {linkContent}
        </Link>
      )}

      {hasChildren && expanded && (
        // Hide the expanded sub-menu only on the DESKTOP collapsed rail (icons-only,
        // 4rem): rendering the indented child rows there overflows the narrow width.
        // Use lg:hidden (not a !collapsed JS guard) so mobile — which shows the full
        // 16rem sidebar regardless of the persisted collapsed flag — still gets it.
        <div className={clsx("ml-4 mt-1 space-y-1", collapsed && "lg:hidden")}>
          {item.children!.map((child) => {
            const childIsActive = isAtOrUnder(location.pathname, child.href);
            return (
              <Link
                key={child.name}
                to={child.href}
                className={clsx(
                  childIsActive ? "nav-item-active" : "nav-item-inactive group",
                  "text-xs py-2",
                  child.disabled && "cursor-not-allowed opacity-60",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-ink-950 rounded-lg"
                )}
                aria-current={childIsActive ? "page" : undefined}
                aria-disabled={child.disabled || undefined}
                onClick={(e) => {
                  if (child.disabled) {
                    e.preventDefault();
                  }
                }}
                title={child.disabled ? t("nav.comingSoon", { name: child.name }) : undefined}
              >
                <child.icon
                  size={16}
                  className={clsx(
                    // Same fix as the parent row's icon above, which this one
                    // was missed by: an ACTIVE child row is .nav-item-active,
                    // i.e. bg-primary-500, so a primary-500 icon on it measured
                    // 1.00:1 in the browser — the glyph and its fill were the
                    // identical yellow. Match the row's own text-ink-950.
                    childIsActive ? "text-ink-950" : "text-ink-400 group-hover:text-primary-500"
                  )}
                />
                <span>{child.name}</span>
                <span
                  className={clsx(
                    "text-xs",
                    childIsActive
                      // No colour on the active branch, for the same reason as
                      // the parent row's nudge: primary-500 on the row's own
                      // primary-500 fill is 1.00:1. Inherit text-ink-950.
                      ? "animate-nav-nudge"
                      : "opacity-0 group-hover:opacity-100 transition-opacity text-ink-400"
                  )}
                  aria-hidden="true"
                >
                  »
                </span>
                {child.disabled && (
                  <span className="ml-auto inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-ink-800 text-ink-300 rounded-full">
                    {t("common.soon")}
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Layout;
